import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle } from '@keobook/ui';
import { formatMoney } from '@keobook/shared';
import { api } from '../lib/api';

/**
 * Hosted invoice page — public, no auth. The :token in the URL is the
 * auth (32 char base64url, ~192 bits of entropy). Customers land here
 * from the email "View invoice & pay" CTA.
 *
 * The page renders a read-only summary + a "Pay now" button that creates
 * a Stripe Checkout Session and 302s the user to Stripe-hosted card
 * collection. ?paid=1 in the URL after Stripe's redirect shows the
 * thank-you state.
 */
type PublicInvoice = Awaited<ReturnType<typeof api.getPublicInvoice>>;

export function PublicInvoice() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const justPaid = params.get('paid') === '1';
  const [state, setState] = useState<
    | { phase: 'loading' }
    | { phase: 'error'; message: string }
    | { phase: 'ready'; data: PublicInvoice }
  >({ phase: 'loading' });
  const [payBusy, setPayBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setState({ phase: 'loading' });
    try {
      const data = await api.getPublicInvoice(token);
      setState({ phase: 'ready', data });
    } catch (err) {
      setState({
        phase: 'error',
        message: err instanceof Error ? err.message : 'Could not load invoice.',
      });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function pay() {
    if (!token) return;
    setPayBusy(true);
    try {
      const { url } = await api.createPublicInvoiceCheckout(token);
      window.location.assign(url);
    } catch (err) {
      setState({
        phase: 'error',
        message: err instanceof Error ? err.message : 'Could not start payment. Please try again.',
      });
      setPayBusy(false);
    }
  }

  if (state.phase === 'loading') {
    return (
      <div style={publicShellStyle}>
        <Card>
          <CardSubtitle>Loading invoice…</CardSubtitle>
        </Card>
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div style={publicShellStyle}>
        <Card>
          <CardTitle>Invoice unavailable</CardTitle>
          <CardSubtitle>{state.message}</CardSubtitle>
        </Card>
      </div>
    );
  }

  const { tenantName, invoice, currency } = state.data;
  const isVoid = invoice.lifecycle === 'void';
  // NGN is record-only — no online "Pay" button (see Stripe/FX constraints).
  const isRecordOnly = currency === 'NGN';
  const fmt = (cents: number) => formatMoney(cents, currency);

  return (
    <div style={publicShellStyle}>
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '0 1rem 3rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: 24,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{tenantName}</div>
            <div style={{ color: '#666', fontSize: 13 }}>Invoice</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{invoice.docNumber}</div>
            <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
              Date {invoice.txnDate} · Due {invoice.dueDate}
            </div>
          </div>
        </div>

        {justPaid && (
          <Card>
            <CardTitle>Thank you — payment received.</CardTitle>
            <CardSubtitle>{tenantName} has been notified. You can close this window.</CardSubtitle>
          </Card>
        )}

        {!justPaid && (
          <Card>
            <table className="kb-report" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th className="kb-report__amt">Qty</th>
                  <th className="kb-report__amt">Unit</th>
                  <th className="kb-report__amt">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.description}</td>
                    <td className="kb-report__amt">{l.qty}</td>
                    <td className="kb-report__amt">{fmt(l.unitPriceCents)}</td>
                    <td className="kb-report__amt">{fmt(l.amountCents)}</td>
                  </tr>
                ))}
                <tr className="kb-report__subtotal">
                  <td colSpan={3}>Subtotal</td>
                  <td className="kb-report__amt">{fmt(invoice.subtotalCents)}</td>
                </tr>
                {invoice.taxCents > 0 && (
                  <tr className="kb-report__subtotal">
                    <td colSpan={3}>Tax</td>
                    <td className="kb-report__amt">{fmt(invoice.taxCents)}</td>
                  </tr>
                )}
                <tr className="kb-report__total">
                  <td colSpan={3}>Total due</td>
                  <td className="kb-report__amt">{fmt(invoice.totalCents)}</td>
                </tr>
              </tbody>
            </table>
            {invoice.customerMemo && (
              <p
                style={{
                  marginTop: 16,
                  fontStyle: 'italic',
                  color: '#444',
                  fontSize: 14,
                }}
              >
                {invoice.customerMemo}
              </p>
            )}
            <div style={{ marginTop: 20 }}>
              {isVoid ? (
                <span className="kb-pill kb-pill--warn">This invoice has been voided.</span>
              ) : isRecordOnly ? (
                <p style={{ color: '#444', fontSize: 14, margin: 0 }}>
                  Amount due <strong>{fmt(invoice.totalCents)}</strong>. Please arrange payment with{' '}
                  {tenantName} directly — online card payment isn’t available for Naira invoices.
                </p>
              ) : (
                <Button onClick={pay} disabled={payBusy}>
                  {payBusy ? 'Loading…' : `Pay ${fmt(invoice.totalCents)}`}
                </Button>
              )}
            </div>
          </Card>
        )}

        <p
          style={{
            color: '#999',
            fontSize: 12,
            textAlign: 'center',
            marginTop: 24,
          }}
        >
          Powered by KEOBOOKS
        </p>
      </div>
    </div>
  );
}

const publicShellStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#f7f7f5',
  paddingTop: 16,
};
